import type { FilesystemJs, OrganizationJs, ShortcutJs } from "@/generated/models";
import { isFilesystemJs, isOrganizationJs, isShortcutJs } from "ls/common/entity-schemas";
import { ShortcutType } from "@/generated/models";
import {
  acceptInviteParameterName,
  commentParameterName,
  documentNodeParameterName,
  folderParameterName,
  inviteParameterName,
  newItemParameterName,
  objectParameterName,
  organizationParameterName,
  routeNames,
  screenParameterName,
  shortcutParameterName,
  workspaceParameterName,
} from "./constants";

export function toHome() {
  return {
    name: routeNames.home,
  };
}

export function toSignIn() {
  return {
    name: routeNames.signIn,
  };
}

export function toRecents() {
  return {
    name: routeNames.recents,
  };
}

export function toManage(options: { new?: boolean } = {}) {
  return {
    name: routeNames.manage,
    query: {
      [newItemParameterName]: options.new ? 1 : undefined,
    },
  };
}

export function toResetPasswordExpired() {
  return {
    name: routeNames.resetPasswordExpired,
  };
}

export function toDidNotCreateAccountExpired() {
  return {
    name: routeNames.didNotCreateAccountExpired,
  };
}
export function toWorkspace(workspace: FilesystemJs | string, options: { invite?: boolean; folderId?: string; accept?: boolean } = {}) {
  const workspaceId = isFilesystemJs(workspace) ? workspace.FilesystemId.toLowerCase() : workspace.toLowerCase();
  return {
    name: routeNames.workspace,
    params: {
      [workspaceParameterName]: workspaceId,
      [folderParameterName]: options.folderId?.toLowerCase(),
    },
    query: {
      [inviteParameterName]: options.invite ? 1 : undefined,
      [acceptInviteParameterName]: options.accept ? 1 : undefined,
    },
  };
}

function getShortcut(file: ShortcutJs | string) {
  return isShortcutJs(file) ? file.Shortcut.toLowerCase() : file.toLowerCase();
}

export function toFileOverview(file: ShortcutJs | string, options: { embed?: boolean; invite?: boolean; comment?: string; node?: string; object?: string } = {}) {
  return {
    name: options.embed ? routeNames.embedFileOverview : routeNames.fileOverview,
    params: { [shortcutParameterName]: getShortcut(file) },
    query: {
      [inviteParameterName]: options.invite ? 1 : undefined,
      [commentParameterName]: options?.comment,
      [documentNodeParameterName]: options?.node?.replaceAll("-", ""),
      [objectParameterName]: options?.object?.replaceAll("-", ""),
    },
  };
}

export function toFileComments(file: ShortcutJs | string) {
  return {
    name: routeNames.fileComments,
    params: {
      [shortcutParameterName]: getShortcut(file),
    },
  };
}

export function toFileHistory(file: ShortcutJs | string) {
  return {
    name: routeNames.fileHistory,
    params: {
      [shortcutParameterName]: getShortcut(file),
    },
  };
}

export function toFilePreview(file: ShortcutJs | string, screen?: string, options: { embed?: boolean; comment?: string } = {}) {
  return {
    name: options.embed ? routeNames.embedFilePreview : routeNames.filePreview,
    params: {
      [shortcutParameterName]: getShortcut(file),
      [screenParameterName]: screen?.toLowerCase(),
    },
    query: { [commentParameterName]: options?.comment },
  };
}

export function toFileInspect(file: ShortcutJs | string, screen?: string, options: { embed?: boolean } = {}) {
  return {
    name: options.embed ? routeNames.embedFileInspect : routeNames.fileInspect,
    params: {
      [shortcutParameterName]: getShortcut(file),
      [screenParameterName]: screen?.toLowerCase(),
    },
  };
}

export function toFileBuild(file: ShortcutJs | string) {
  return {
    name: routeNames.fileBuild,
    params: {
      [shortcutParameterName]: getShortcut(file),
    },
  };
}

export function toFileNewProjectOfSameType(file: ShortcutJs) {
  if (file.ShortcutType === ShortcutType.Whiteboard) return toFileNewWhiteboard();
  if (file.ShortcutType === ShortcutType.Document) return toFileNewDocument();
  if (file.ShortcutType === ShortcutType.Design) return toFileNewSim();
  return {
    name: routeNames.fileNew,
  };
}

export function toFileNewWhiteboard() {
  return {
    name: routeNames.fileNewWhiteboard,
  };
}

export function toFileNewDocument() {
  return {
    name: routeNames.fileNewDocument,
  };
}

export function toFileNewSim() {
  return {
    name: routeNames.fileNewSim,
  };
}

function getOrganization(org: OrganizationJs | string) {
  return isOrganizationJs(org) ? org.Id.toLowerCase() : org.toLowerCase();
}

export function toOrganizationMembers(organization: OrganizationJs | string) {
  return {
    name: routeNames.settingsOrganizationMembers,
    params: {
      [organizationParameterName]: getOrganization(organization),
    },
  };
}

export function toOrganizationSettings(organization: OrganizationJs | string) {
  return {
    name: routeNames.settingsOrganizationGeneral,
    params: {
      [organizationParameterName]: getOrganization(organization),
    },
  };
}

export function toOrganizationAuthenticationSettings(organization: OrganizationJs | string) {
  return {
    name: routeNames.settingsOrganizationAuthentication,
    params: {
      [organizationParameterName]: getOrganization(organization),
    },
  };
}

export function toOrganizationBilling(organization: OrganizationJs | string) {
  return {
    name: routeNames.settingsOrganizationBilling,
    params: {
      [organizationParameterName]: getOrganization(organization),
    },
  };
}

export function toOnPremSettingsEmail(organization: OrganizationJs | string) {
  return {
    name: routeNames.onPremSettingsEmail,
    params: {
      [organizationParameterName]: getOrganization(organization),
    },
  };
}

export function toOnPremSettingsLicenseKey(organization: OrganizationJs | string) {
  return {
    name: routeNames.onPremSettingsLicenseKey,
    params: {
      [organizationParameterName]: getOrganization(organization),
    },
  };
}

export function toSsoAuthenticationSettings(organization: OrganizationJs | string) {
  return {
    name: routeNames.settingsOrganizationAuthenticationSso,
    params: {
      [organizationParameterName]: getOrganization(organization),
    },
  };
}

export function toActiveDirectoryAuthenticationSettings(organization: OrganizationJs | string) {
  return {
    name: routeNames.settingsOrganizationAuthenticationActiveDirectory,
    params: {
      [organizationParameterName]: getOrganization(organization),
    },
  };
}

export function toLdapAuthenticationSettings(organization: OrganizationJs | string) {
  return {
    name: routeNames.settingsOrganizationAuthenticationLdap,
    params: {
      [organizationParameterName]: getOrganization(organization),
    },
  };
}

export function toOnPremSetupDatabaseConnection() {
  return {
    name: routeNames.onPremSetupDatabaseConnection,
  };
}

export function toEmbedSignInComplete() {
  return {
    name: routeNames.embedSignInComplete,
  };
}
